"use client";

import { useState, useEffect, useSyncExternalStore } from "react";
import { createPortal } from "react-dom";
import { formatPrice, formatStrikethroughPrice } from "@/lib/format-price";
import { resolvePriceClaimDisplay } from "@/lib/price-math";
import { convertEurTo } from "@/lib/currency-convert";
import { useSiteContext } from "@/components/providers/SiteContextProvider";
import { getTranslations } from "@/lib/i18n";
// Catalog-voucher sticky-bar gate (Codex finding on PR #1193, coordinator
// redirect-not-block decision 2026-08-15): same shared resolver
// VoucherHeroPrice/StickyVoucherCTA use on the voucher page itself, so the
// pax caption here can never drift from the canonical voucher copy.
import { resolveVoucherPaxLabel } from "@/lib/voucher-pax-label";
import { readCustomerPriceClaim } from "@/lib/strikethrough-gate";

export default function DealDetailClient({ price, priceHistoryGate, discountPercentage, referencePrice, isRoundtripDiscountDeal = false, bookingHref, isRoundtrip = false, isVoucherDeal = false, voucherHref, hasFixedDisplayPrice = false, dealTitle, dealId, dealImage, travelPeriod }) {
  const { currency, effectiveRate, locale, key: marketKey } = useSiteContext();
  const toCurrency = (eur) => convertEurTo(eur, currency, effectiveRate);
  const { t } = getTranslations(locale);
  const priceHistoryClaim = readCustomerPriceClaim(
    { price_history_gate: priceHistoryGate, is_multi_leg: isRoundtripDiscountDeal, discount_percentage: discountPercentage, lowest_price_calculated: referencePrice },
    marketKey,
    { currentPriceEur: price },
  );
  const discount =
    priceHistoryClaim?.displayRule === "badge" || priceHistoryClaim?.displayRule === "configured_percentage"
      ? priceHistoryClaim.savingsPct
      : 0;
  const originalPrice =
    priceHistoryClaim?.displayRule === "absolute_amount"
      ? priceHistoryClaim.pMin30Eur
      : priceHistoryClaim?.displayRule === "configured_percentage"
        ? priceHistoryClaim.referencePriceEur
      : null;
  const savingsEur =
    priceHistoryClaim?.displayRule === "absolute_amount"
      ? priceHistoryClaim.savingsEur
      : null;
  // Eén gedeelde beslissing over de hele prijsclaim (Dennis 28-09-2026,
  // "B + akkoord"; zelfde helper als de dealpagina en DealCard): van-prijs in
  // hele eenheden omlaag, het DE-bespaarbedrag uit de GETOONDE getallen
  // (nooit hoger dan de backend). Valt de van-prijs niet meer boven de
  // getoonde nu-prijs, dan vervalt de HELE claim in de balk.
  // Uitzondering (Dennis 28-09-2026, "de korting mag blijven staan"): zou
  // de afgeronde van-prijs niet boven de nu-prijs liggen maar de exacte
  // referentie wel, dan exact op centen (vanPrecision "cents"); pas als
  // ook die niet erboven ligt vervalt de claim. Zie price-math.js.
  const claimDisplay = resolvePriceClaimDisplay({
    displayRule: priceHistoryClaim?.displayRule,
    originalAmount: toCurrency(originalPrice),
    currentAmount: toCurrency(price),
    savingsAmount: toCurrency(savingsEur),
  });
  const [showBar, setShowBar] = useState(false);
  // Render the fixed bar through a portal to <body> so that no transformed /
  // contained / will-change ancestor anywhere in the deal-page tree can turn
  // position:fixed into ancestor-relative (which made it scroll away instead of
  // staying pinned on mobile, Dennis 2026-06-04). The mounted gate avoids an
  // SSR/client mismatch (document is client-only).
  // false on the server + during hydration, true once on the client, without a
  // setState-in-effect (SSR-safe + lint-clean).
  const mounted = useSyncExternalStore(
    () => () => {},
    () => true,
    () => false,
  );

  // GA4: view_item event when deal detail page loads
  useEffect(() => {
    if (typeof window !== "undefined" && window.gtag && dealTitle) {
      window.gtag("event", "view_item", {
        currency,
        value: price || 0,
        items: [{ item_name: dealTitle, item_id: dealId }],
      });
    }
  }, [currency, dealTitle, dealId, price]);

  useEffect(() => {
    function handleScroll() {
      const scrollPosition = window.scrollY + window.innerHeight;
      const pageHeight = document.body.scrollHeight;
      const showStart = 1400;
      const footerStart = pageHeight - 200;

      if (scrollPosition < showStart) {
        setShowBar(false);
      } else if (scrollPosition >= showStart && scrollPosition < footerStart) {
        setShowBar(true);
      } else {
        setShowBar(false);
      }
    }
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  if (!price) return null;

  // Catalog vouchers are priced as a TOTAL for 2 people, not "per
  // persoon" — no adults/children counts are threaded through from the
  // deal-detail page (that campaign data isn't fetched there), so this
  // resolves via resolveVoucherPaxLabel's own documented 2-adults/
  // 0-children default.
  //
  // Codex round-1 finding on PR #1194 (BLOCKING, fixed): only assert
  // that copy when the caller confirms `price` is genuinely a total
  // (hasFixedDisplayPrice — sourced from page.js's own fixed_display_price
  // check). At least one live catalog voucher (id 244, confirmed via
  // the detail door this page reads) has no fixed_display_price and
  // `price` falls back to lowest_price, a PER-PERSON figure (PR #1193's
  // own finding) — relabeling that number "totaal voor 2 personen"
  // would present an unconfirmed per-person price as a confirmed total.
  // Falls back to the untouched, pre-existing "per persoon" wording
  // rather than asserting something unverified.
  const priceCaption =
    isVoucherDeal && hasFixedDisplayPrice ? resolveVoucherPaxLabel(t) : t("deal.per_persoon");

  const bar = (
    <div
      className="fixed left-1/2 -translate-x-1/2 z-[1000] transition-[bottom] duration-300 w-full"
      style={{
        maxWidth: "1000px",
        bottom: showBar ? "0px" : "-200px",
        paddingBottom: "env(safe-area-inset-bottom, 0px)",
      }}
    >
      <div
        style={{
          boxShadow: "rgba(0, 0, 0, 0.24) 0px 3px 8px",
          borderRadius: "6px",
          background: "white",
          border: "1px solid #e7e7f4",
        }}
      >
        <div className="px-4 sm:px-6">
          {(() => {
          const showPriceClaimCells = Boolean(priceHistoryClaim) && !isVoucherDeal && claimDisplay.showClaim;
          const showOriginalPrice =
            showPriceClaimCells &&
            (priceHistoryClaim?.displayRule === "absolute_amount" || priceHistoryClaim?.displayRule === "configured_percentage") &&
            originalPrice > 0 &&
            claimDisplay.vanAmount !== null;
          return (
          <div
            className="grid items-center py-3"
            style={{
              // With DE amount claim: 4 cells (van | nu vanaf | bespaar | CTA).
              // With NL badge claim: 3 cells (nu vanaf | korting | CTA).
              // Without claim: only nu vanaf + CTA render. Use a 3-fr layout with
              // an empty leading column so the nu-vanaf cell sits in the middle of
              // the row, visually centered relative to the bar (Kartik #98). The
              // CTA stays in the right column with its own justify-end.
              gridTemplateColumns: showPriceClaimCells
                ? showOriginalPrice
                  ? "1fr 1fr 1fr 1fr"
                  : "1fr 1fr 1fr"
                : "1fr 1fr 1fr",
            }}
          >
            {/* "van" — strikethrough original price */}
            {showOriginalPrice && (
              <div className="flex items-center">
                <div>
                  <p className="text-sm text-gray-700">{t("deal.price.van")}</p>
                  <h3 className="font-bold text-lg sm:text-xl">
                    <del>
                      {formatStrikethroughPrice(claimDisplay.vanAmount, {
                        currency,
                        includeSymbol: true,
                        locale,
                        precision: claimDisplay.vanPrecision,
                      })}
                    </del>
                  </h3>
                </div>
              </div>
            )}

            {/* "nu vanaf" — sale price.
             * Without discount, only this cell + the CTA render. The grid is
             * `1fr 1fr 1fr`; we explicitly place the price in column 2 (middle
             * third) so the CTA naturally lands in column 3 (right third) and
             * column 1 stays as left-side whitespace — the price reads as
             * visually centred in the bar (Kartik #98). `justify-center` then
             * centres the inner block within its own column. With discount > 0
             * the cell sits next to "van" + "korting" in a 4-fr layout, so we
             * keep the default placement + left alignment within the cell. */}
            <div
              className={`flex items-center ${showPriceClaimCells ? "" : "justify-center"}`}
              style={showPriceClaimCells ? undefined : { gridColumnStart: 2 }}
            >
              {/* Inner block uses `text-center` so the label, price and
               * "per persoon" subline all align horizontally on a single
               * vertical centre line — matches the centred layout the
               * outer flex wrapper produces (Kartik #99, 2026-04-27). */}
              <div className="text-center">
                <p className="text-sm text-gray-700">{t("deal.price.nu_vanaf")}</p>
                <h3 className="font-bold text-lg sm:text-xl">
                  {formatPrice(toCurrency(price), { currency, includeSymbol: true, locale })}
                </h3>
                <span className="text-sm text-gray-500">{priceCaption}</span>
              </div>
            </div>

            {/* Price-history claim — percentage for NL, absolute saving for DE. */}
            {showPriceClaimCells && (
              <div className="flex items-center">
                <div>
                  <p className="text-sm text-gray-700">
                    {priceHistoryClaim.displayRule === "absolute_amount"
                      ? t("deal.price.savings_amount_label", "Bespaar")
                      : t("deal.price.korting")}
                  </p>
                  <h3 className="font-bold text-lg sm:text-xl">
                    {priceHistoryClaim.displayRule === "absolute_amount"
                      ? formatPrice(claimDisplay.savingsAmount, {
                          currency,
                          includeSymbol: true,
                          locale,
                        })
                      : `-${discount}%`}
                  </h3>
                </div>
              </div>
            )}

            {/* CTA button — matches old site: orange gradient, 55px height, 4px radius.
                Roundtrip (multi-leg) deals: booking is a separate, not-yet-built
                phase (BookingWizard is single-hotel/pick-one and can't represent a
                multi-stop trip). Dennis 2026-07-23 ("Boeken werkt niet"): show a
                graceful "binnenkort boekbaar" notice instead of a link into that
                broken flow. Single-stay deals (isRoundtrip=false) are unchanged. */}
            <div className="flex items-center justify-end">
              {isRoundtrip ? (
                <span
                  className="flex items-center justify-center text-center text-[#F56A00] font-bold whitespace-normal flex-grow sm:flex-grow-0 rounded border border-orange-200 bg-orange-50"
                  style={{
                    minHeight: "55px",
                    padding: "8px 16px",
                    fontSize: "13px",
                    minWidth: "140px",
                  }}
                >
                  {t("deal.roundtrip.coming_soon_title", "Binnenkort boekbaar")}
                </span>
              ) : isVoucherDeal ? (
                // Catalog voucher: same voucher-purchase CTA as the
                // inline pricing block above (page.js), linking to the
                // bare voucherHref (no kind=deal-voucher) rather than
                // bookingHref — see the gate comment on page.js's
                // isCatalogVoucher for the full rationale.
                <a
                  href={voucherHref}
                  className="flex items-center justify-center text-white font-bold hover:opacity-90 transition-opacity whitespace-nowrap flex-grow sm:flex-grow-0"
                  style={{
                    background: "linear-gradient(#ff8b33, #f56a00)",
                    height: "55px",
                    borderRadius: "4px",
                    padding: "0 24px",
                    fontSize: "15px",
                    minWidth: "140px",
                  }}
                >
                  {t("voucher.cta_order")}
                </a>
              ) : (
                <a
                  href={bookingHref}
                  className="flex items-center justify-center text-white font-bold hover:opacity-90 transition-opacity whitespace-nowrap flex-grow sm:flex-grow-0"
                  style={{
                    background: "linear-gradient(#ff8b33, #f56a00)",
                    height: "55px",
                    borderRadius: "4px",
                    padding: "0 24px",
                    fontSize: "15px",
                    minWidth: "140px",
                  }}
                >
                  {t("deal.cta.kies_datum")}
                </a>
              )}
            </div>
          </div>
          );
          })()}
        </div>
      </div>
    </div>
  );

  return mounted ? createPortal(bar, document.body) : null;
}
